/**
 * The tenancies with the most money outstanding.
 *
 * It takes the HEAD of the list as the server sent it. The reconciliation service
 * already orders `tenancies` by highest open total first (then reference, then id),
 * so this ranking is the backend's, not a client re-sort — which also means the card
 * cannot disagree with the table below it about who owes most.
 *
 * Org-wide, like the other two cards in this row: it reads the full response rather
 * than the filtered view, and the section heading says so.
 */
import React from "react";
import { ListOrdered } from "lucide-react";

import { fmtMoney } from "../bexioFormat";

export default function BexioTopOpenCard({ tenancies, currency, limit = 5, onSelect }) {
  const all = Array.isArray(tenancies) ? tenancies : [];
  // Only rows that actually owe something: a zero-open tenancy at the top of a
  // "highest open first" list would mean nothing is outstanding at all.
  const rows = all.filter((r) => Number(r.open_total || 0) !== 0).slice(0, limit);

  return (
    <section
      aria-labelledby="bexio-top-open-title"
      /* `min-w-0` is load-bearing, not decoration. As a grid item this section
         defaults to `min-width: auto`, so it cannot shrink below its own min-content
         — and the rows below contain a `whitespace-nowrap` amount plus long
         references. Measured at 390px: without it the card sat at 464px inside a
         374px track and pushed the WHOLE PAGE to a 480px horizontal scroll. */
      className="min-w-0 rounded-xl border border-border bg-card p-5"
    >
      <div className="mb-3 flex items-center gap-2">
        <ListOrdered aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
        <h3
          id="bexio-top-open-title"
          className="text-sm font-semibold text-foreground"
        >
          Höchste offene Beträge
        </h3>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Keine offenen Beträge in den berücksichtigten Rechnungen.
        </p>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => {
            const label =
              r.tenancy_reference_number || r.tenant_display_name || "—";
            const sub = [r.tenant_display_name, r.unit_label, r.room_label]
              .filter(Boolean)
              .join(" · ");
            const body = (
              <>
                <span className="w-4 shrink-0 text-xs tabular-nums text-muted-foreground">
                  {i + 1}.
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {label}
                  </span>
                  {/* Absent labels stay absent rather than being filled in: an
                      unresolvable tenancy is a data-quality signal, and the
                      warnings card is where it is explained. */}
                  <span className="block truncate text-xs text-muted-foreground">
                    {sub || "—"}
                  </span>
                </span>
                <span className="shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums text-foreground">
                  {fmtMoney(r.open_total, currency)}
                </span>
              </>
            );
            return (
              <li key={r.tenancy_id}>
                {onSelect ? (
                  <button
                    type="button"
                    onClick={() => onSelect(r)}
                    aria-label={`Details zu ${label} anzeigen`}
                    className="flex w-full items-start gap-2 rounded-lg px-1 py-1 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {body}
                  </button>
                ) : (
                  <div className="flex items-start gap-2 px-1 py-1">{body}</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
